import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { UserPlus, CheckCircle, XCircle, Users, Calendar, Filter, UserCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import statisticsService from '../../services/statisticsService';
import { users as userService } from '../../services/apiService';
import { Loader } from '../UI/LoadingSpinner';
import ProfileLink from '../UI/ProfileLink';
import DateRangePicker from '../UI/DateRangePicker';
import Avatar from '../UI/Avatar';
import { Pagination, Autocomplete } from '@anyemp/ui-kit';
import StatisticsTable from './StatisticsTable';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const StatCard = ({ title, count, icon: Icon, colorClass }) => (
  <div className={`rounded-xl border-2 p-5 shadow-sm transition-shadow hover:shadow-md ${colorClass}`}>
    <div className="flex items-center gap-4">
      <div className="p-3 rounded-xl bg-white/60 dark:bg-black/30">
        <Icon className="h-7 w-7" />
      </div>
      <div>
        <p className="text-sm font-medium opacity-90">{title}</p>
        <p className="text-3xl font-bold mt-0.5">{count}</p>
      </div>
    </div>
  </div>
);

const TalentListTable = ({ items, columns, emptyMessage = 'No data', footer }) => {
  const rowKey = useCallback(
    (item) => `${item.talent_id ?? 'x'}-${item.occurred_at ?? ''}-${item.from_status ?? ''}-${item.to_status ?? ''}`,
    []
  );
  const tableColumns = useMemo(
    () => columns.map((col) => ({
      ...col,
      render: col.render ? col.render : (row) => row[col.key],
    })),
    [columns]
  );
  return (
    <StatisticsTable
      columns={tableColumns}
      data={items || []}
      rowKey={rowKey}
      variant="ghost"
      emptyState={
        <div className="py-8 px-4 text-center">
          <p className="text-gray-500 dark:text-gray-400 text-sm">{emptyMessage}</p>
        </div>
      }
      footer={footer}
    />
  );
};

const OnboardingDashboard = () => {
  const location = useLocation()
  const onboardingManagerRowKey = useCallback((row) => row.manager_id ?? '_unknown', []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    manager_ids: []
  });
  const [transitionsPage, setTransitionsPage] = useState(1);
  const transitionsPerPage = 20;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.manager_ids?.length) params.manager_ids = filters.manager_ids;
      const result = await statisticsService.getOnboardingStats(params);
      setData(result?.data || result);
    } catch (err) {
      console.error('Error loading onboarding stats:', err);
      setError(err.message || 'Failed to load onboarding statistics');
    } finally {
      setLoading(false);
    }
  }, [filters.startDate, filters.endDate, filters.manager_ids]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setTransitionsPage(1);
  }, [filters.startDate, filters.endDate, filters.manager_ids]);

  useEffect(() => {
    const fetchManagers = async () => {
      try {
        setManagersLoading(true);
        const response = await userService.getAllUsers();
        const usersList = response?.data?.users || response?.users || response?.data || response;
        const managerOptions = Array.isArray(usersList)
          ? usersList.map(user => ({
              value: String(user.id),
              label: user.name || user.fullName || user.email || user.id
            }))
          : [];
        setManagers(managerOptions);
      } catch (err) {
        console.error('Error fetching managers:', err);
        toast.error('Failed to load managers list');
      } finally {
        setManagersLoading(false);
      }
    };
    fetchManagers();
  }, []);

  if (loading && !data) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader variant="component" size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg">
        {error}
        <button onClick={loadData} className="ml-3 underline text-sm">
          Retry
        </button>
      </div>
    );
  }

  const counts = data?.counts || { entered: 0, passed: 0, failed: 0 };
  const byManager = data?.by_manager || [];
  const lists = data?.lists || { entered_onboarding: [], passed_onboarding: [], failed_onboarding: [] };
  const transitionsLog = data?.transitions_log || [];

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Onboarding Progress
        </h2>
        <button
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors"
        >
          <Filter className="h-4 w-4" />
          <span>Filters</span>
        </button>
      </div>

      {/* Filters */}
      {filtersExpanded && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Calendar className="inline h-4 w-4 mr-1" />
                Period
              </label>
              <DateRangePicker
                startDate={filters.startDate}
                endDate={filters.endDate}
                onChange={(range) => setFilters((f) => ({ ...f, startDate: range.startDate || '', endDate: range.endDate || '' }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Users className="inline h-4 w-4 mr-1" />
                Managers
              </label>
              {managersLoading ? (
                <div className="flex items-center justify-center h-10">
                  <Loader size="sm" />
                </div>
              ) : (
                <Autocomplete
                  multiple
                  clearable
                  fullWidth
                  options={managers}
                  value={filters.manager_ids}
                  onChange={(value) => setFilters((f) => ({ ...f, manager_ids: value || [] }))}
                  placeholder="All managers"
                />
              )}
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={() => setFiltersExpanded(false)}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <StatCard
          title="Entered Onboarding"
          count={counts.entered}
          icon={UserPlus}
          colorClass="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200"
        />
        <StatCard
          title="Passed"
          count={counts.passed}
          icon={CheckCircle}
          colorClass="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200"
        />
        <StatCard
          title="Failed"
          count={counts.failed}
          icon={XCircle}
          colorClass="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200"
        />
      </div>

      {byManager.length > 0 && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/30 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <UserCircle className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              By Manager
            </h3>
          </div>
          <StatisticsTable
            columns={[
              {
                key: 'manager',
                label: 'Manager',
                render: (row) => (
                  <div className="flex flex-wrap items-center gap-2">
                    <Avatar
                      avatar={row.manager?.avatar}
                      name={row.manager?.name || row.manager?.fullName}
                      email={row.manager?.email}
                      size="sm"
                    />
                    <span className="text-sm text-gray-900 dark:text-gray-100">
                      {row.manager?.name || row.manager?.fullName || row.manager?.email || (row.manager_id != null ? `ID ${row.manager_id}` : '—')}
                    </span>
                  </div>
                )
              },
              {
                key: 'entered',
                label: 'Entered',
                render: (row) => <span className="text-sm text-gray-900 dark:text-gray-100">{row.entered}</span>
              },
              {
                key: 'passed',
                label: 'Passed',
                render: (row) => <span className="text-sm text-green-600 dark:text-green-400 font-medium">{row.passed}</span>
              },
              {
                key: 'failed',
                label: 'Failed',
                render: (row) => <span className="text-sm text-red-600 dark:text-red-400 font-medium">{row.failed}</span>
              }
            ]}
            data={byManager}
            rowKey={onboardingManagerRowKey}
            variant="ghost"
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Entered Onboarding */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/30 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              Entered Onboarding
            </h3>
          </div>
          <TalentListTable
            items={lists.entered_onboarding}
            columns={[
              { key: 'talent_name', label: 'Talent', render: (r) => r.talent_id ? <ProfileLink to={`/talents/${r.talent_id}`} state={{ from: location.pathname + location.search }}>{r.talent_name || `ID ${r.talent_id}`}</ProfileLink> : r.talent_name },
              { key: 'occurred_at', label: 'Date', render: (r) => formatDate(r.occurred_at) }
            ]}
            emptyMessage="No talents entered onboarding in the selected period"
          />
        </div>

        {/* Right: Passed + Failed stacked */}
        <div className="flex flex-col gap-6">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/30 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-green-50/50 dark:bg-green-900/10">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                Passed
              </h3>
            </div>
            <TalentListTable
              items={lists.passed_onboarding}
              columns={[
                { key: 'talent_name', label: 'Talent', render: (r) => r.talent_id ? <ProfileLink to={`/talents/${r.talent_id}`} state={{ from: location.pathname + location.search }}>{r.talent_name || `ID ${r.talent_id}`}</ProfileLink> : r.talent_name },
                { key: 'to_status', label: 'To' },
                { key: 'occurred_at', label: 'Date', render: (r) => formatDate(r.occurred_at) }
              ]}
              emptyMessage="No talents passed onboarding in the selected period"
            />
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/30 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-red-50/50 dark:bg-red-900/10">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                Failed
              </h3>
            </div>
            <TalentListTable
              items={lists.failed_onboarding}
              columns={[
                { key: 'talent_name', label: 'Talent', render: (r) => r.talent_id ? <ProfileLink to={`/talents/${r.talent_id}`} state={{ from: location.pathname + location.search }}>{r.talent_name || `ID ${r.talent_id}`}</ProfileLink> : r.talent_name },
                { key: 'to_status', label: 'To' },
                { key: 'occurred_at', label: 'Date', render: (r) => formatDate(r.occurred_at) }
              ]}
              emptyMessage="No talents failed onboarding in the selected period"
            />
          </div>
        </div>
      </div>

      {/* Transitions Log — full width, paginated */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/30 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            Transitions Log
          </h3>
        </div>
        <TalentListTable
          items={transitionsLog.slice((transitionsPage - 1) * transitionsPerPage, transitionsPage * transitionsPerPage)}
          columns={[
            { key: 'talent_name', label: 'Talent', render: (r) => r.talent_id ? <ProfileLink to={`/talents/${r.talent_id}`} state={{ from: location.pathname + location.search }}>{r.talent_name || `ID ${r.talent_id}`}</ProfileLink> : r.talent_name },
            { key: 'from_status', label: 'From' },
            { key: 'to_status', label: 'To' },
            { key: 'occurred_at', label: 'Date', render: (r) => formatDate(r.occurred_at) }
          ]}
          emptyMessage="No onboarding transitions in the selected period"
          footer={transitionsLog.length > transitionsPerPage ? (
            <Pagination
              currentPage={transitionsPage}
              totalPages={Math.ceil(transitionsLog.length / transitionsPerPage)}
              totalItems={transitionsLog.length}
              itemsPerPage={transitionsPerPage}
              onPageChange={setTransitionsPage}
              showResults="range"
            />
          ) : null}
        />
      </div>
    </div>
  );
};

export default OnboardingDashboard;
